/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Box, Button, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { CustomCheckList } from "@components/CustomCheckList/CustomCheckList";
import { usePostValidateNextStageMutation } from "@api/applications";
import { InactivityHandler } from "@components/InactivityHandler";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import { STATUS_CATALOG, VISUAL_CONFIG_CODES } from "@helpers/constantes";
import { DEFAULT_CHECKLIST_EXPEDIENTE_PM } from "@helpers/visualConfig";
import { ChecklistSectionDocuments, GuaranteeType } from "@interfaces/catalogs";
import { useLazyGetVisualConfigQuery } from "@api/configuration";
import { useLazyGetGuaranteeTypeQuery } from "@api/catalogs";
import { filterChecklistByGuarantee } from "@components/CustomCheckList/functions";
import React from "react";

const ListaRevision = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checklist, setChecklist] = useState<ChecklistSectionDocuments>([]);
  const [loadingChecklist, setLoadingChecklist] = useState<boolean>(false);

  const application = useSelector((state: RootState) => state.register.application);
  const companyData = useSelector((state: RootState) => state.register.company);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const dispatch = useAppDispatch();

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getVisualConfig] = useLazyGetVisualConfigQuery();
  const [getGuaranteeType] = useLazyGetGuaranteeTypeQuery();

  const handleOnClickNext = async () => {
    try {
      setIsLoading(true);

      const { documentos_dictamen } = STATUS_CATALOG;

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: documentos_dictamen.code,
        username: `BOFU - ${accountUser.email}`,
      }).unwrap();

      navigate(documentos_dictamen.url);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setChecklist(DEFAULT_CHECKLIST_EXPEDIENTE_PM);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (!application.tax_system_id) {
          return;
        }

        setLoadingChecklist(true);

        // obtener los tipos de garantia y los documentos de la configuracion
        const [guarantees, checklist] = await Promise.all([
          getGuaranteeType().unwrap(),
          getVisualConfig({
            tax_system_id: application.tax_system_id,
            code: VISUAL_CONFIG_CODES.CHECKLIST_EXPEDIENTE,
          }).unwrap(),
        ]);

        if (checklist && checklist.length) {
          // identificar el tipo de garantia, en caso de ser necesario
          const guarantee_type: GuaranteeType | undefined = application.guarantee_type_id
            ? guarantees.find((item) => item.id === application.guarantee_type_id)
            : undefined;

          // filtar por garantia
          const filtered_checklist = filterChecklistByGuarantee(checklist as ChecklistSectionDocuments, guarantee_type);
          // ordenar por orden
          filtered_checklist.sort((a, b) => (a.order || 0) - (b.order || 0));
          setChecklist(filtered_checklist);
        }
      } catch (error) {
        console.error("Ha ocurrido un error al obtener los documentos de la configuracion", error);
      } finally {
        setLoadingChecklist(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <InactivityHandler />
        <Box textAlign={"center"} mx={4}>
          <Typography
            mt={4}
            mb={3}
            textAlign={"center"}
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={theme?.palette?.fontWeights?.semibold}
            color={theme?.palette?.primary?.dark}
          >
            ¡Hola {companyData.legal_name || companyData.name || "Nombre del Cliente"}!
          </Typography>

          <Typography
            mb={3}
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
          >
            Ahora completa el expediente digital. <br /> Conforme avances, deberás cargar los siguientes documentos, si
            no los tienes a la mano puedes guardar tu avance y completarlo después
          </Typography>

          {loadingChecklist ? (
            <Skeleton variant="rectangular" sx={{ height: "200px", width: "100%", borderRadius: 2, my: 3 }} />
          ) : (
            <React.Fragment>
              <Box display={"flex"} flexDirection={"column"} gap={2} my={3}>
                {checklist.map((item) => (
                  <CustomCheckList
                    key={item.title}
                    id={item.title}
                    title={item.title}
                    tooltip={item.tooltip}
                    items={item.documents}
                  />
                ))}
              </Box>
              <Button variant="default" onClick={handleOnClickNext} disabled={isLoading}>
                Continuar
              </Button>
            </React.Fragment>
          )}
        </Box>
      </Grid>
      {isMobileOrTablet == false && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default ListaRevision;
